import Card from 'apollo-react/components/Card';
import CardContent from 'apollo-react/components/CardContent';
import CardHeader from 'apollo-react/components/CardHeader';
import Container from 'apollo-react/components/Container';
import Grid from 'apollo-react/components/Grid';
import Typography from 'apollo-react/components/Typography';
import parse from 'html-react-parser';
import { marked } from 'marked';
import { React, useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { API_URL, SYS_ROLE } from '../common/Constants';
import DetailDialog from '../common/DetailDialog';
import Dialog from '../common/Dialog';
import Loading from '../common/Loading';
import { getApi, postApi, setURL, redirectToLogin, CustomApiError } from '../common/Utils';
import authData from '../json/authData.json';
import userIdData from '../json/userIdData.json';
import funcroleData from '../json/funcroleData.json';
import noticeData from '../json/noticeData.json';
import auditCategoryAudit from '../json/auditCategoryAudit.json';
import chatbotAnswer from '../json/chatbotAnswer.json';
import sessionEvaluationData from '../json/sessionEvaluationData.json';
import chatHist from '../json/chatHist.json';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import './Portal.scss';

let isError = false;

/**
 * ホーム
 */
function Portal() {
	// ===== 定数定義 =====
	const DIALOG_STATUS = {
		NO_DIALOG: '0',
		OK_DIALOG: '1',
		DETAIL_DIALOG: '2',
		REDIRECT_DIALOG: '3',
		NO_DATA_DIALOG: '4',
		FEEDBACK_DIALOG: '5',
	};
	// ===== 状態管理セクション =====
	const location = useLocation();
	const navigate = useNavigate();
	const [t, i18n] = useTranslation();
	// UI制御用状態
	const [isLoading, setIsLoading] = useState(true);
	const [maintenanceFlg, setMaintenanceFlg] = useState(false);
	// ダイアログ制御用状態
	const [dialogStatus, setDialogStatus] = useState(DIALOG_STATUS.NO_DIALOG);
	const [dialogOpen, setDialogOpen] = useState(false);
	const [dialogContent, setDialogContent] = useState('');
	const [detailContent, setDetailContent] = useState('');
	//ポータルのお知らせ情報制御用
	const [portalList, setPortalList] = useState([]);
	// チャットボット制御用状態
	const [isChatOpen, setIsChatOpen] = useState(false);
	const [currentLevel, setCurrentLevel] = useState(0); // 0: 閉じている, 1: 利用規約, 2: カテゴリ選択, 3: サブカテゴリ選択, 4: チャット
	const [hasInteracted, setHasInteracted] = useState(false); 
	const [isGenerating, setIsGenerating] = useState(false);
	const messagesEndRef = useRef(null);

	// チャットウィンドウのサイズ管理を追加（オプション）の下に追加
	const [chatSize, setChatSize] = useState({ width: '70vw', height: '82vh' });
	const [isMaximized, setIsMaximized] = useState(false);
	const [isResizing, setIsResizing] = useState(false);
	const [resizeDirection, setResizeDirection] = useState(null);
	const [initialSize, setInitialSize] = useState({ width: 0, height: 0 });
	const [initialPos, setInitialPos] = useState({ x: 0, y: 0 });

	// チャットデータ用状態
	const [categories, setCategories] = useState([]);
	const [subCategories, setSubCategories] = useState({});
	const [categoriesLoaded, setCategoriesLoaded] = useState(false);
	const [selectedCategory, setSelectedCategory] = useState(null);
	const [selectedSubCategory, setSelectedSubCategory] = useState(null);
	const [formValues, setFormValues] = useState({
		inputText: '',
	});
	const [messages, setMessages] = useState([
		{ id: 1, type: 'bot', text: 'こんにちは！どのようにお手伝いできますか？' },
	]);
	const [messageFeedbacks, setMessageFeedbacks] = useState({});
	const [questionHistory, setQuestionHistory] = useState([]);
	const [sessionStartDate, setSessionStartDate] = useState(null);

	// ===== ユーティリティ関数 =====
	// 時刻をフォーマットする関数
	const formatTime = (timestamp) => {
		if (!timestamp) return '';
		
		const date = new Date(timestamp);
		const hours = date.getHours().toString().padStart(2, '0');
		const minutes = date.getMinutes().toString().padStart(2, '0');
		
		return `${hours}:${minutes}`;
	};

	/**
	 * 共通エラー処理
	 * @param {object} error エラーオブジェクト
	 * @param {string} detailErrorMessage 詳細ダイアログに設定するエラーメッセージ
	 */
	const commonError = (error, detailErrorMessage) => {
		if (error.name === 'MaintenanceApiError') {
			setMaintenanceFlg(true);
		} else if (error.name === 'CustomApiError') {
			const message = error.message;
			setDialogStatus(DIALOG_STATUS.OK_DIALOG);
			setDialogOpen(true);
			setDialogContent(message);
		} else {
			setDialogStatus(DIALOG_STATUS.DETAIL_DIALOG);
			setDialogOpen(true);
			setDialogContent(detailErrorMessage);
			setDetailContent(error.stack);
		}
	};

	/**
	 * ダイアログでOKボタン押下時の処理
	 */
	const onOk = () => {
		setDialogOpen(false);
	};

	// リサイズ開始
	const handleResizeStart = (direction, e) => {
		e.preventDefault();
		setIsResizing(true);
		setResizeDirection(direction);
		setInitialPos({ x: e.clientX, y: e.clientY });
		
		// ビューポート単位を実際のピクセル値に変換
		const container = e.target.closest('.chat-container');
		const currentWidth = container.offsetWidth;
		const currentHeight = container.offsetHeight;
		
		setInitialSize({
			width: currentWidth,
			height: currentHeight
		});
	};

	function redirectExpired(){
		localStorage.removeItem("reAuthFlag");
        console.log('redirect to ' + API_URL.LOGIN_INDEX);
        window.location.href = API_URL.LOGIN_INDEX;
	}

	// ===== API成功/失敗ハンドラー =====
	/**
	 * 認証APIの成功時の処理
	 */
	function authApiSuccess(response) {
		localStorage.setItem('token', JSON.stringify(response));
	};

	/**
	 * 認証APIの失敗時の処理
	 * @param {object} error エラーオブジェクト
	 */
	function authApiError(error) {
		const detailErrorMessage = '認証処理でエラーが発生しました。\n(An error occurred during the authentication process.)';
		commonError(error, detailErrorMessage);
		redirectToLogin();
	};

	/**
	 * 機能権限情報取得APIの成功時の処理
	 */
	function funcroleApiSuccess(response) {
		localStorage.setItem('funcroleList', JSON.stringify(response));
	};

	/**
	 * 機能権限情報取得APIの失敗時の処理
	 * @param {object} error エラーオブジェクト
	 */
	function funcroleApiError(error) {
		const detailErrorMessage = '機能権限情報取得処理でエラーが発生しました。\n(An error occurred during the function authority information acquisition process.)';
		commonError(error, detailErrorMessage);
		//認証期限切れの場合は再ログイン
		redirectToLogin();
	};

	/**
	 * userid取得APIの成功時の処理
	 */
	function userIdApiSuccess(response) {
		localStorage.setItem('userid', JSON.stringify(response));
		localStorage.setItem('usrTypeCode', JSON.stringify(response));
		localStorage.setItem('email', JSON.stringify(response));
		localStorage.setItem('usrroleCode', JSON.stringify(response));
	};

	/**
	 * userid取得APIの失敗時の処理
	 * @param {object} error エラーオブジェクト
	 */
	function userIdApiError(error) {
		let detailErrorMessage = "";
		if (error.message.includes("Account expired")) {
			detailErrorMessage = t('message.1001510');
			setDialogStatus(DIALOG_STATUS.REDIRECT_DIALOG);
			setDialogOpen(true);
			setDialogContent(detailErrorMessage);
		}
		else
		{
			commonError(error,error.message);
			isError = true;
		}
	};

	function noticeApiSuccess(response) {
		setPortalList(response);
	};

	function noticeApiError(error) {
		// メンテナンスエラーの場合は、エラーダイアログを表示せずにメンテナンスフラグを立てる
		if (error.name === 'MaintenanceApiError') {
			setMaintenanceFlg(true);
			return; // ここで処理を終了
		}

		let detailErrorMessage = "";
		if (error.message.includes("Account expired")) {
			detailErrorMessage = t('message.1001510');
		}
		else
		{
			if(error.message.includes("Please login again"))
			{
				detailErrorMessage = '認証もしくはセッションの有効期限が切れました。再ログインしてください。\r\nAuthentication has expired. Please login again.';
			}
			else
			{
				detailErrorMessage = 'お知らせ情報取得処理でエラーが発生しました。\n(An error occurred during the process of acquiring notification information.)';
			}
		}
		setDialogStatus(DIALOG_STATUS.REDIRECT_DIALOG);
		setDialogOpen(true);
		setDialogContent(detailErrorMessage);
	};

	/**
	 * チャットボット問い合わせAPIの成功時の処理
	 */
	function questionApiSuccess(response) {
		const questionHistGuid = response?.questionHistGuid;
		const aiAnswer = response?.aiAnswer;

		// AIの回答をメッセージに追加
		if (aiAnswer) {
			const botResponse = { 
				id: Date.now(), // ユニークなIDを生成
				type: 'bot', 
				text: aiAnswer,
				timestamp: new Date(), // 現在時刻
				questionHistGuid: questionHistGuid // questionHistGuidを保存
			};
			setMessages(prevMessages => [...prevMessages, botResponse]);

			// チャットでやり取りが完了したフラグを設定
			setHasInteracted(true);
		}
	
		// 生成中状態をオフにする
		setIsGenerating(false);
	};

	/**
	 * チャットボット問い合わせAPIの失敗時の処理
	 * @param {object} error エラーオブジェクト
	 */
	function questionApiError(error) {
		const detailErrorMessage = 'チャットボットからの回答取得でエラーが発生しました。\n(An error occurred while getting chatbot answer.)';
		commonError(error, detailErrorMessage);

		// 生成中状態をオフにする
		setIsGenerating(false);

		//認証期限切れの場合は再ログイン											
		redirectToLogin();
	};

	/**
	 * サービスカテゴリ取得APIの成功時の処理
	 */
	function getAuditCatSuccess(response) {
		// 1層目のサービスカテゴリを設定
		const categoryList = response.map(item => ({
			id: item.auditCatCode,
			name: item.auditCatName
		}));
		setCategories(categoryList);
	
		// 2層目のサブカテゴリ（サービス）を設定
		const subCategoryMap = {};
		response.forEach(item => {
			subCategoryMap[item.auditCatCode] = item.responseAudit.map(audit => ({
				id: audit.auditCode,
				name: audit.auditName
			}));
		});
		setSubCategories(subCategoryMap);
		setCategoriesLoaded(true);
	};

	/**
	 * サービスカテゴリ取得APIの失敗時の処理
	 */
	function getAuditCatError(error) {
		const detailErrorMessage = 'サービスカテゴリ取得でエラーが発生しました。\n(An error occurred while getting category information.)';
		commonError(error, detailErrorMessage);
		setCategoriesLoaded(true);
		redirectToLogin();
	};

	/**
	 * お問い合わせ履歴取得APIの成功時の処理
	 */
	function getHistoryApiSuccess(response) {
		setQuestionHistory(response || []);
	};

	/**
	 * お問い合わせ履歴取得APIの失敗時の処理
	 */
	function getHistoryApiError(error) {
		const detailErrorMessage = 'お問い合わせ履歴取得でエラーが発生しました。\n(An error occurred while getting question history.)';
		commonError(error, detailErrorMessage);
		// エラー時は空配列をセット
		setQuestionHistory([]);
	};

	/**
	 * 評価登録APIの成功時の処理
	 */
	function evaluationApiSuccess(response) {
		console.log('評価登録が完了しました:', response);
	}

	/**
	 * 評価登録APIの失敗時の処理
	 * @param {object} error エラーオブジェクト
	 */
	function evaluationApiError(error) {
		// JSONパースエラーの場合は成功として扱う（APIが空レスポンスを返す場合）
		if (error.message && error.message.includes('Unexpected end of JSON input')) {
			console.log('評価登録が完了しました（空レスポンス）');
			return;
		}
		
		const detailErrorMessage = '評価登録でエラーが発生しました。\n(An error occurred while registering evaluation.)';
		commonError(error, detailErrorMessage);
		console.error('評価登録エラー:', error);
	}

	/**
	 * セッション評価APIの成功時の処理
	 */
	function sessionEvaluationApiSuccess(response) {
		console.log('セッション評価API成功:', response);
		// responseが直接文字列でメールアドレスが返される場合
		const contactEmail = response?.contactEmail || '';
		return contactEmail;
	}

	/**
	 * セッション評価APIの失敗時の処理
	 * @param {object} error エラーオブジェクト
	 */
	function sessionEvaluationApiError(error) {
		const detailErrorMessage = 'セッション評価でエラーが発生しました。\n(An error occurred while registering session evaluation.)';
		commonError(error, detailErrorMessage);
		console.error('セッション評価エラー:', error);
		return '';
	}

	// ===== イベントハンドラー =====
	// 履歴アイテムクリック時の処理関数
	const handleHistoryClick = (historyItem) => {
		// デバッグ用ログを追加
		console.log('履歴アイテム:', historyItem);
		console.log('questionHistGuid:', historyItem.questionHistGuid);
		
		// 入力ボックスに表示する代わりに、チャットメッセージとして表示
		const userMessage = {
			id: Date.now(),
			type: 'user',
			text: historyItem.usrQuestion,
			timestamp: new Date(),
			isHistoryMessage: true // 履歴メッセージであることを示すフラグ
		};

		const botMessage = {
			id: Date.now() + 1,
			type: 'bot',
			text: historyItem.aiAnswer || '回答が見つかりませんでした。',
			timestamp: new Date(),
			isHistoryMessage: true, // 履歴メッセージであることを示すフラグ
			questionHistGuid: historyItem.questionHistGuid, // 履歴のquestionHistGuidを保持
			evaluation: historyItem.evaluation // 履歴の評価情報を保持
		};

		// 現在のメッセージに履歴メッセージを追加
		setMessages(prevMessages => [...prevMessages, userMessage, botMessage]);

		// 履歴メッセージの評価状態を復元
		if (historyItem.evaluation) {
			const evaluationType = historyItem.evaluation === "1" ? 'up' : 'down';
			setMessageFeedbacks(prevFeedbacks => ({
				...prevFeedbacks,
				[botMessage.id]: evaluationType
			}));
		}
	};

	const handleMessageFeedback = async (messageId, type) => {
		// 対象のメッセージを取得
		const targetMessage = messages.find(msg => msg.id === messageId);
		if (!targetMessage || !targetMessage.questionHistGuid) {
			console.error('メッセージまたはquestionHistGuidが見つかりません');
			return;
		}
	
		let evaluationValue = "";

		// 同じメッセージで同じ評価を再度クリックした場合は評価を取り消す
		if (messageFeedbacks[messageId] === type) {
			const newFeedbacks = { ...messageFeedbacks };
			delete newFeedbacks[messageId];
			setMessageFeedbacks(newFeedbacks);
			evaluationValue = "";
		} else {
			// 新しい評価を設定
			setMessageFeedbacks({
				...messageFeedbacks,
				[messageId]: type
			});
			evaluationValue = type === 'up' ? "1" : "0"; // よい評価は"1"、よくない評価は"0"
		}

		// 評価API呼び出し
		const ansEvaluationUrl = setURL(API_URL.CHATBOT_ANSWEREVALUATEION);
		const ansEvaluationParams = {};
		ansEvaluationParams['questionHistGuid'] = targetMessage.questionHistGuid;
		ansEvaluationParams['questionEvaluation'] = evaluationValue;
		const ansEvaluationParam = String(new URLSearchParams(ansEvaluationParams));
		const ansEvaluationApi = {
			url: ansEvaluationUrl + '?' + ansEvaluationParam,
			sysRole: SYS_ROLE.CHATBOT,
			success: evaluationApiSuccess,
			error: evaluationApiError
		}
		
		try {
			await getApi(ansEvaluationApi);
			console.log(`Message ${messageId} received ${type} feedback, evaluation: ${evaluationValue}`);
		} catch (error) {
			console.error('評価API呼び出しエラー:', error);
		}
	};

	// 利用規約に同意しない場合の処理
	const declineTerms = () => {
		// チャットボットを完全に閉じる
		setIsChatOpen(false);
		setCurrentLevel(0);
	};

	/**
	 * フィードバックダイアログでOKボタン押下時の処理
	 */
	const onFeedbackDialogOk = () => {
		setDialogOpen(false);
		// チャット画面はそのまま維持（何もしない）
	};

	/**
	 * メールアドレスをクリップボードにコピーする関数
	 */
	const copyToClipboard = (text) => {
		navigator.clipboard.writeText(text).then(() => {
			// コピー成功時の処理（オプション）
			console.log('メールアドレスをコピーしました:', text);
			// 必要に応じてトースト通知などを表示
		}).catch(err => {
			console.error('コピーに失敗しました:', err);
		});
	};

	// ===== チャットフロー制御 =====
	// チャットを開始する関数
	const startChat = async () => {
		setIsChatOpen(true);
		setCurrentLevel(1); // 1階層目を表示
	};

	// 利用規約に同意する関数
	const agreeToTerms = async () => {
		setCurrentLevel(2); // カテゴリ選択画面に進む

		// サービスカテゴリ取得API実行
		const getAuditCatUrl = setURL(API_URL.CHATBOT_GETAUDITCAT);
		const getAuditCatApi = {
			url: getAuditCatUrl,
			sysRole: SYS_ROLE.CHATBOT,
			success: getAuditCatSuccess,
			error: getAuditCatError,
			json: auditCategoryAudit,
		}
		await getApi(getAuditCatApi);
	};

	// カテゴリを選択する関数
	const selectCategory = (categoryId) => {
		setSelectedCategory(categoryId);
		setCurrentLevel(3); // サブカテゴリ（サービス）選択画面を表示
	};

	// windowClose 関数の下に追加
	const toggleMaximize = () => {
		if (isMaximized) {
			// 元のサイズに戻す
			setChatSize({ width: '70vw', height: '82vh' });
			setIsMaximized(false);
		} else {
			// 最大化
			setChatSize({ width: '90vw', height: '90vh' });
			setIsMaximized(true);
		}
	};

	// 言語変更を監視するuseEffectをトップレベルに移動
    useEffect(() => {
        if (currentLevel === 4 && messages.length > 0 && messages[0].isInitialMessage) {
            const category = categories.find(c => c.id === selectedCategory);
            const subCategory = subCategories[selectedCategory]?.find(sc => sc.id === selectedSubCategory);
            
            if (category && subCategory) {
                setMessages(prevMessages => [
                    {
                        ...prevMessages[0],
                        text: t('chatbotGreeting', {
                            categoryName: category.name,
                            productName: subCategory.name
                        })
                    },
                    ...prevMessages.slice(1)
                ]);
            }
        }
    }, [i18n.language, currentLevel, selectedCategory, selectedSubCategory, categories, subCategories, t]); // 依存配列を追加

	// サブカテゴリ（audit）を選択してチャットを開始する関数
	const selectSubCategory = async (subCategoryId) => {
		setSelectedSubCategory(subCategoryId);
		setCurrentLevel(4); // チャット開始

		// セッション開始時刻を記録
		setSessionStartDate(new Date());

		// 初期メッセージを設定
		const category = categories.find(c => c.id === selectedCategory);
		const subCategory = subCategories[selectedCategory].find(sc => sc.id === subCategoryId);

		setMessages([
			{
				id: 1,
				type: 'bot',
				text: t('chatbotGreeting', {
					categoryName: category.name,
					productName: subCategory.name
				}),
				timestamp: new Date(),
				isInitialMessage: true
			}
		]);

		// お問い合わせ履歴取得API実行
		const getHistoryUrl = setURL(API_URL.CHATBOT_GETHIST);
		const getChatHistParams = {};
		getChatHistParams['auditCode'] = subCategoryId;
		const getChatHistParam = String(new URLSearchParams(getChatHistParams));
		const getHistoryApi = {
			url: getHistoryUrl + '?' + getChatHistParam,
			sysRole: SYS_ROLE.CHATBOT,
			success: getHistoryApiSuccess,
			error: getHistoryApiError,
			json: chatHist,
		}
		await getApi(getHistoryApi);
	};

	// メッセージ送信時の処理関数
	const sendMessage = async () => {
		var text = formValues.inputText;
		if (text.trim() === '') return;

		// ユーザーメッセージをリストに追加
		const newUserMessage = { 
			id: messages.length + 1, 
			type: 'user', 
			text: text,
			timestamp: new Date()
		};
		setMessages([...messages, newUserMessage]);

		// 生成中状態をオンにする
		setIsGenerating(true);

		// 選択されたカテゴリとサブカテゴリから値を取得
		const auditCode = selectedSubCategory; // サブカテゴリのIDをauditCodeとして使用
		const auditName = subCategories[selectedCategory]?.find(sc => sc.id === selectedSubCategory)?.name || '';

		// チャットボット問い合わせAPI
		const questionUrl = setURL(API_URL.CHATBOT_QUESTION);
		let formData = new FormData();
		formData.append('auditCode', auditCode);
		formData.append('auditName', auditName);
		
		// Base64エンコーディング（TextEncoderを使用）
		const encoder = new TextEncoder();
		const uint8Array = encoder.encode(text);
		const encodedQuestion = btoa(String.fromCharCode(...uint8Array));
		formData.append('question', encodedQuestion);

		const postApiParam = {
			url: questionUrl,
			sysRole: SYS_ROLE.CHATBOT,
			body: formData,
			success: questionApiSuccess,
			error: questionApiError,
			json: chatbotAnswer
		};
		await postApi(postApiParam);

		setFormValues({ ...formValues, inputText: '' });
	};

	// チャットボットの表示/非表示を切り替える関数
	const toggleChat = () => {
		setIsChatOpen(!isChatOpen);
	};
	
	// チャットボットに問い合わせがない場合のダイアログOKボタン押下時の処理
	const onNoDataDialogOk = () => {
		setDialogOpen(false);
		// チャットボット画面を完全に閉じる
		setIsChatOpen(false);
		setCurrentLevel(0);
		setSessionStartDate(null);
		setFormValues({
			inputText: '',
		});
		setSelectedCategory(null);
		setSelectedSubCategory(null);
		setMessages([]);
		setMessageFeedbacks({});
		setQuestionHistory([]);
		setIsGenerating(false);
		setCategoriesLoaded(false); // カテゴリ読み込み状態もリセット
	};

	// チャット送信時に自動で最下部にスクロール
	useEffect(() => {
		scrollToBottom();
	}, [messages, isGenerating]);

	// 一番下にスクロールする関数
	const scrollToBottom = () => {
		messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
	};

	// 戻るボタンの処理
	const handleBack = () => {
		if (currentLevel === 1) {
			// 利用規約から戻る場合はチャットを閉じる
			setIsChatOpen(false);
			setCurrentLevel(0);
		} else if (currentLevel === 2) {
			// カテゴリ選択から戻る場合は利用規約に戻る
			setCurrentLevel(1);
		} else if (currentLevel === 3) {
			// サブカテゴリ選択から戻る場合はカテゴリ選択に戻る
			setCurrentLevel(2);
			setSelectedCategory(null);
		} else if (currentLevel === 4) {
			// チャット中から戻る場合はサブカテゴリ選択に戻る
			setCurrentLevel(3);
			setSelectedSubCategory(null);
			setMessages([]);
		}
	};

	/**
	 * チャットボット問い合わせ後の評価登録の処理
	 */
	const handleReviewClick = async (value) => {
		const sessionEndDate = new Date();
		let contactEmail = '';

		// セッション評価API呼び出し
		try {
			const evaluationUrl = setURL(API_URL.CHATBOT_SESSIONEVALUATEION);
			const evaluationParams = {};
			evaluationParams['auditCode'] = selectedSubCategory;
			evaluationParams['sessionEvaluation'] = value === 1 ? "1" : "0";
			evaluationParams['sessionStartDate'] = sessionStartDate ? sessionStartDate.toISOString() : new Date().toISOString();
			evaluationParams['sessionEndDate'] = sessionEndDate.toISOString();
			
			const getEvaluationParam = String(new URLSearchParams(evaluationParams));
			const getEvaluationApiParam = {
				url: evaluationUrl + '?' + getEvaluationParam,
				sysRole: SYS_ROLE.CHATBOT,
				success: (response) => {
					contactEmail = response?.contactEmail || '';
					console.log('取得したcontactEmail:', contactEmail);
				},
				error: sessionEvaluationApiError,
				json: sessionEvaluationData
			};
			
			await getApi(getEvaluationApiParam);
		} catch (error) {
			console.error('セッション評価API呼び出しエラー:', error);
		}	

		// メッセージ設定
		let message;
		let dialogContentElement;
		
		if (value === 1) {
			message = t('message.1006010');
			dialogContentElement = (
				<div className="dialog-custom-content">
					<div className="dialog-flex-container">
						<div className="dialog-message-column">
							<p className="dialog-message" style={{ whiteSpace: 'pre-line' }}>{message}</p>
						</div>
						<div className="dialog-image-column">
							<span style={{ fontSize: '40px' }}>🤖</span>
						</div>
					</div>
				</div>
			);
		}
		else if (value === 2) {
			message = t('message.1006020');
			dialogContentElement = (
				<div className="dialog-custom-content">
					<div className="dialog-flex-container">
						<div className="dialog-message-column">
							<p className="dialog-message" style={{ whiteSpace: 'pre-line' }}>{message}</p>
							{contactEmail && (
								<div style={{ 
									marginTop: '15px',
									display: 'flex',
									alignItems: 'center',
									gap: '10px',
									padding: '10px',
									backgroundColor: '#f5f5f5',
									borderRadius: '4px'
								}}>
									<span style={{ 
										flex: 1,
										fontWeight: 'bold',
										color: '#333'
									}}>
										{contactEmail}
									</span>
									<Tooltip 
										title={t('copyEmail')} 
										placement="top"
										arrow
									>
										<IconButton 
											size="small"
											onClick={() => {
												navigator.clipboard.writeText(contactEmail).then(() => {
													console.log('Copied to clipboard:', contactEmail);
												}).catch(err => {
													console.error('Failed to copy:', err);
												});
											}}
											style={{
												color: '#086ebc',
												padding: '8px'
											}}
										>
											<svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
												<path d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z"/>
											</svg>
										</IconButton>
									</Tooltip>
								</div>
							)}
						</div>
						<div className="dialog-image-column">
							<span style={{ fontSize: '40px' }}>🤖</span>
						</div>
					</div>
				</div>
			);
		}
		
		setDialogStatus(DIALOG_STATUS.FEEDBACK_DIALOG);
		setDialogOpen(true);
		setDialogContent(dialogContentElement);
	}

	const windowClose = () => {
		setIsChatOpen(false);
		setCurrentLevel(0);
		setSessionStartDate(null);
		setIsMaximized(false); // 追加
		setChatSize({ width: '70vw', height: '82vh' });
		
		// フォーム値をリセット
		setFormValues({
			inputText: '',
		});
		
		// その他の状態もリセット
		setSelectedCategory(null);
		setSelectedSubCategory(null);
		setMessages([]);
		setMessageFeedbacks({});
		setQuestionHistory([]);
		setIsGenerating(false);
		setHasInteracted(false);
	}	

	 // ===== useEffect =====
	 useEffect(() => {
		(async () => {
			if (location.state !== null) {
				setMaintenanceFlg(true);
				setIsLoading(false);
				return;
			}

			//表示URLから認可コード取得
			const queryString = new URLSearchParams(document.location.search);
			var code = queryString.get('code');
			if (process.env.NODE_ENV !== 'production') {
				//ローカル起動のため、認証なしで動作させるためセット
				code = 'dev';
			}

			//認証済みの状態でお知らせ画面を開く、認可コードをlocalStorageにセットしていない、もしくは直近で取得した認可コードと相違する場合は再度認証処理を行う
			if (code !== null) {
				if ((window.localStorage.getItem(['code']) === null) || (window.localStorage.getItem(['code']) !== code)) {
					localStorage.setItem('code', code);
					// アクセストークン取得API
					const urlAuth = setURL(API_URL.TOKEN);
					const getAuthApiParam = {
						url: `${urlAuth}?code=${code}`,
						sysRole: SYS_ROLE.PORTAL,
						success: authApiSuccess,
						error: authApiError,
						json: authData,
					}
					await getApi(getAuthApiParam);

					//userid取得API
					const urlUserId = setURL(API_URL.USERID);
					const getUserIDApiParam = {
						url: urlUserId,
						sysRole: SYS_ROLE.PORTAL,
						success: userIdApiSuccess,
						error: userIdApiError,
						json: userIdData,
					}
					await getApi(getUserIDApiParam);

					if(isError)
					{
						setIsLoading(false);
						return;
					}

					// ローカルストレージからuseridを取得し、存在する場合のみパースする
					const storedUserIdData = localStorage.getItem('userid');
					const storedUserId = storedUserIdData ? JSON.parse(storedUserIdData).userid : null;
					if (storedUserId) {
						// 機能権限情報取得API
						const urlFuncrole = setURL(API_URL.FUNCROLE);
						const getFuncroleApiParam = {
							url: urlFuncrole,
							sysRole: SYS_ROLE.PORTAL,
							success: funcroleApiSuccess,
							error: funcroleApiError,
							json: funcroleData,
						}
						await getApi(getFuncroleApiParam);
					}
					else
					{
						let detailErrorMessage = t('message.1001510');
						setDialogStatus(DIALOG_STATUS.REDIRECT_DIALOG);
						setDialogOpen(true);
						setDialogContent(detailErrorMessage);
					}
				}
			}

			// お知らせ情報取得API
			// ローカルストレージからuseridを取得し、存在する場合のみパースする
			const url = setURL(API_URL.NOTICE);
			const getNoticeApiParam = {
				url: url,
				sysRole: SYS_ROLE.PORTAL,
				success: noticeApiSuccess,
				error: noticeApiError,
				json: noticeData
			}
			await getApi(getNoticeApiParam);

			//バーのリンクが表示されないため、ポータル画面再表示
			navigate('/portal');
			setIsLoading(false);
		})();
	}, []);

	// カテゴリ読み込み完了時のチェック用useEffect
	useEffect(() => {
		if (currentLevel === 2 && categoriesLoaded && categories.length === 0) {
			// カテゴリが存在しない場合
			setDialogStatus(DIALOG_STATUS.NO_DATA_DIALOG);
			setDialogOpen(true);
			setDialogContent(t('message.1006000'));
			// ダイアログ表示後に前の画面に戻る
			setTimeout(() => {
				setCurrentLevel(1);
			}, 100);
		}
	}, [currentLevel, categoriesLoaded, categories.length, t]);

	// サブカテゴリチェック用useEffect
	useEffect(() => {
		if (currentLevel === 3 && selectedCategory && 
			subCategories[selectedCategory] && subCategories[selectedCategory].length === 0) {
			// サブカテゴリが存在しない場合
			setDialogStatus(DIALOG_STATUS.NO_DATA_DIALOG);
			setDialogOpen(true);
			setDialogContent(t('message.1006000'));
			// ダイアログ表示後に前の画面に戻る
			setTimeout(() => {
				setCurrentLevel(2);
				setSelectedCategory(null);
			}, 100);
		}
	}, [currentLevel, selectedCategory, subCategories, t]);

	// リサイズ中
	useEffect(() => {
		if (!isResizing) return;

		const handleMouseMove = (e) => {
			const deltaX = e.clientX - initialPos.x;
			const deltaY = e.clientY - initialPos.y;
			
			let newWidth = initialSize.width;
			let newHeight = initialSize.height;

			// 方向に応じてサイズを計算
			if (resizeDirection.includes('e')) newWidth = initialSize.width + deltaX;
			if (resizeDirection.includes('w')) newWidth = initialSize.width - deltaX;
			if (resizeDirection.includes('s')) newHeight = initialSize.height + deltaY;
			if (resizeDirection.includes('n')) newHeight = initialSize.height - deltaY;

			// 最小・最大サイズの制限
			newWidth = Math.max(400, Math.min(newWidth, window.innerWidth * 0.95));
			newHeight = Math.max(500, Math.min(newHeight, window.innerHeight * 0.95));

			setChatSize({
				width: `${newWidth}px`,
				height: `${newHeight}px`
			});
		};

		const handleMouseUp = () => {
			setIsResizing(false);
			setResizeDirection(null);
		};

		document.addEventListener('mousemove', handleMouseMove);
		document.addEventListener('mouseup', handleMouseUp);

		return () => {
			document.removeEventListener('mousemove', handleMouseMove);
			document.removeEventListener('mouseup', handleMouseUp);
		};
	}, [isResizing, resizeDirection, initialPos, initialSize]);

	// ===== レンダリング =====
	if (isLoading) {
		// ロード中の場合はローディング画面
		return (
			<Loading />
		);
	} else {
		return (
			<Container className="portal" maxWidth="md">
				<Typography variant="subtitle" gutterBottom>
					{t('information')}
				</Typography>
				<Grid container spacing={2}>
					{maintenanceFlg && (
						<Grid item xs={12} gap={2}>
							<Card className="card">
								<CardHeader title={t('message.1001500')} />
							</Card>
						</Grid>
					)}
					{!maintenanceFlg && portalList.map((portal, index) => (
						<Grid key={index} item xs={12} gap={2}>
							<Card className="card">
								<CardHeader title={parse(marked.parseInline(portal.title))} />
								<CardContent>
									<Typography variant="body2">
										{parse(marked.parseInline(portal.comment))}
									</Typography>
								</CardContent>
							</Card>
						</Grid>
					))}
					{!maintenanceFlg && portalList.length === 0 && (
						<Grid item xs={12} gap={2}>
							<Card className="card">
								<CardHeader title={t('message.1000000')} />
							</Card>
						</Grid>
					)}
				</Grid>
				{/* ダイアログ定義セクション */}
				{dialogStatus === DIALOG_STATUS.OK_DIALOG && (
					<Dialog
						isOpen={dialogOpen}
						content={dialogContent}
						onOk={onOk}
					/>
				)}
				{dialogStatus === DIALOG_STATUS.NO_DATA_DIALOG && (
					<Dialog
						isOpen={dialogOpen}
						content={dialogContent}
						onOk={onNoDataDialogOk}
					/>
				)}
				{dialogStatus === DIALOG_STATUS.DETAIL_DIALOG && (
					<DetailDialog
						isOpen={dialogOpen}
						content={dialogContent}
						detailContent={detailContent}
						onOk={onOk}
					/>
				)}
				{dialogStatus === DIALOG_STATUS.REDIRECT_DIALOG && (
					<Dialog
						isOpen={dialogOpen}
						content={dialogContent}
						onOk={redirectExpired}
					/>
				)}
				{dialogStatus === DIALOG_STATUS.FEEDBACK_DIALOG && (
					<Dialog
						isOpen={dialogOpen}
						content={dialogContent}
						onOk={onFeedbackDialogOk}
					/>
				)}

				<div className="portal-container">
					{/* 2行分のスペースを追加 */}
    				<div style={{ marginTop: '3rem' }}></div>
				<h3>{t('aiInquiry')}</h3>
				{/* お知らせの一番下に左寄せでチャットボタンを配置 */}
				<div className="chat-button-container">
					<button
						className="chat-button"
						onClick={startChat}
						aria-label="AIチャットを開始"
					>
						{isChatOpen ? '×' : '💬'}
					</button>
				</div>

				{/* チャットボットコンポーネント - isChatOpenがtrueの場合のみ表示 */}
				{isChatOpen && (
					<>
						<div className="chat-overlay"></div>
						<div 
							className="chat-container"
							style={{
								width: chatSize.width,
								height: chatSize.height
							}}
						>
							<div className="chat-header">
								{/* 最大化/復元ボタン - Windows 11 スタイル */}
								<IconButton className='maximize' onClick={toggleMaximize}>
									{isMaximized ? (
										// 復元ボタン（2つの重なった四角 - Windows 11スタイル）
										<svg
											xmlns="http://www.w3.org/2000/svg"
											viewBox="0 0 24 24"
											width="20"
											height="20"
											fill="none"
											stroke="white"
											strokeWidth="1.5"
											strokeLinecap="round"
											strokeLinejoin="round"
										>
											{/* 後ろの四角 */}
											<rect x="8" y="4" width="12" height="12" />
											{/* 前の四角 */}
											<path d="M4 8h12v12H4z" fill="white" fillOpacity="0.3" />
											<rect x="4" y="8" width="12" height="12" />
										</svg>
									) : (
										// 最大化ボタン（シンプルな四角 - Windows 11スタイル）
										<svg
											xmlns="http://www.w3.org/2000/svg"
											viewBox="0 0 24 24"
											width="20"
											height="20"
											fill="none"
											stroke="white"
											strokeWidth="1.5"
										>
											<rect x="4" y="4" width="16" height="16" />
										</svg>
									)}
								</IconButton>
								
								{/* 既存の閉じるボタン */}
								<IconButton className='close' onClick={windowClose}>
									<svg
										xmlns="http://www.w3.org/2000/svg"
										viewBox="0 0 24 24"
										width="20"
										height="20"
										fill="none"
										stroke="white"
										strokeWidth="1.5"
										strokeLinecap="round"
										strokeLinejoin="round"
									>
										<line x1="18" y1="6" x2="6" y2="18"></line>
										<line x1="6" y1="6" x2="18" y2="18"></line>
									</svg>
								</IconButton>
								
								<h3>
									{currentLevel === 1 ? 
										`🤖 ${t('termsOfUse.header')}` :  
										`${t('aiInquiry')}`
									}
								</h3>
							</div>

							{/* 利用規約・注意事項（1階層目） */}
							{currentLevel === 1 && (
								<div className="terms-agreement">
									{/* <h3>AIChatBotサービス利用に関する注意事項</h3> */}
									<div className="terms-content">
											<p>{t('termsOfUse.l1')}</p>
											<p>{t('termsOfUse.l2')}</p>
											<p>{t('termsOfUse.l3')}</p>
											<p>{t('termsOfUse.l4')}</p>
											<p>{t('termsOfUse.l5')}</p>
											<p>{t('termsOfUse.l6')}</p>
											<p>{t('termsOfUse.l7')}</p>
											<br />
											<p>{t('termsOfUse.footer')}</p>
									</div>
									<div className="terms-buttons">
										<button
											className="category-button decline-button"
											onClick={declineTerms}
										>
											{t('declineAndClose')}
										</button>
										<button
											className="category-button agree-button"
											onClick={agreeToTerms}
										>
											{t('agreeAndContinue')}
										</button>
									</div>
								</div>
							)}

							{/* カテゴリ選択（2階層目） */}
							{currentLevel === 2 && (
							<div className="category-selection">
								<h3>{t('selectProductCategory')}</h3>
								
								<div className="category-buttons-container">
								{categories.map(category => (
									<button
									key={category.id}
									className="category-button"
									onClick={() => selectCategory(category.id)}
									>
									{category.name}
									</button>
								))}
								
								{/* 戻るボタンをコンテナ内に移動 */}
								<button
									className="category-button back-button"
									onClick={handleBack}
								>
									{t('returnButton')}
								</button>
								</div>
							</div>
							)}

							{/* サブカテゴリ選択（3階層目） */}
							{currentLevel === 3 && selectedCategory && (
							<div className="category-selection">
								<h3>
								{t('selectProduct', { 
									categoryName: categories.find(c => c.id === selectedCategory)?.name 
								})}
							</h3>
								
								<div className="category-buttons-container">
								{subCategories[selectedCategory]?.map(subCategory => (
									<button
									key={subCategory.id}
									className="category-button"
									onClick={() => selectSubCategory(subCategory.id)}
									>
									{subCategory.name}
									</button>
								))}
								
								{/* 戻るボタンをコンテナ内に移動 */}
								<button
									className="category-button back-button"
									onClick={handleBack}
								>
									{t('returnButton')}
								</button>
								</div>
							</div>
							)}

							{/* チャットメッセージ（4階層目） */}
							{currentLevel === 4 && (
								<>
									<div className='chat-header-category'>
										<div className='category-column'>
											<h3>{t('chatbotInquiryHeader')}</h3>
											<>
												{t('productIntroPhrase', { 
													productName: subCategories[selectedCategory].find(sc => sc.id === selectedSubCategory).name 
												})}
											</>&emsp;
											<button 
												className='back-products feedback-button positive'
												onClick={handleBack}
											>
												{t('backToProducts')}
											</button>
										</div>
										<div className='history-items'>
											{questionHistory.length > 0 ? (
												questionHistory.slice(0, 10).map((item, index) => (
													<div 
														key={index} 
														className='history-item'
														onClick={() => handleHistoryClick(item)}
														style={{ cursor: 'pointer' }}
													>
														<span className='history-summary' title={item.usrQuestion}>
															{item.usrQuestion && item.usrQuestion.length > 50 
																? `${item.usrQuestion.substring(0, 50)}...` 
																: item.usrQuestion
															}
														</span>
													</div>
												))
											) : (
												<div className='history-item'>
													<span className='history-summary'>{t('noChatHistory')}</span>
												</div>
											)}
										</div>
									</div>

									{/* 区切り線を追加 */}
									<div className="chat-divider"></div>

									<div className="chat-messages">
										{messages.map((message) => (
											<div
												key={message.id}
												className={message.type === 'bot' ? 'bot-message' : 'user-message'}
											>
												{message.type === 'bot' && (
													<div className="bot-icon">
														<span role="img" aria-label="ロボット">🤖</span>
													</div>
												)}

												<div className="message-content">
													<div className="message-text-container">
														<p style={{ 
															whiteSpace: 'pre-line',
															wordWrap: 'break-word',
															wordBreak: 'break-word',
															maxWidth: '100%',
															lineHeight: '1.4'
														}}>
															{message.text}
														</p>
														<span className="message-timestamp">
															{formatTime(message.timestamp)}
														</span>
													</div>

													{/* botメッセージの場合のみ評価ボタンを表示 */}
													{message.type === 'bot' && !message.isInitialMessage && (
														<div className="message-feedback">
															<button
																className={`feedback-thumb thumb-up ${messageFeedbacks[message.id] === 'up' ? 'active' : ''}`}
																onClick={() => handleMessageFeedback(message.id, 'up')}
																aria-label="役に立った"
																// disabled={message.isHistoryMessage} // 履歴メッセージの場合は無効化（オプション）
															>
																👍
															</button>
															<button
																className={`feedback-thumb thumb-down ${messageFeedbacks[message.id] === 'down' ? 'active' : ''}`}
																onClick={() => handleMessageFeedback(message.id, 'down')}
																aria-label="役に立たなかった"
																// disabled={message.isHistoryMessage} // 履歴メッセージの場合は無効化（オプション）
															>
																👎
															</button>
														</div>
													)}
												</div>
											</div>
										))}

										{/* 回答生成中の表示 */}
										{isGenerating && (
											<div className="generating-message">
												<p>回答を生成中です...</p>
												<span className="loading-dots">
													<span className="dot"></span>
													<span className="dot"></span>
													<span className="dot"></span>
												</span>
											</div>
										)}

										{/* スクロール用の空div */}
										<div ref={messagesEndRef} />
									</div>

									{/* 入力エリア */}
									<div className="chat-input-container">
										<div className="input-feedback-row">
											{/* 入力部分 */}
											<div className="input-section">
											<div className="input-with-button">
												<textarea
													className="chat-input-field"
													placeholder={t('chatPlaceHolder', { 
														productName: subCategories[selectedCategory]?.find(sc => sc.id === selectedSubCategory)?.name || 'DDD'
													})}
													value={formValues.inputText}
													onChange={(event) => {
														setFormValues({ ...formValues, inputText: event.target.value });
													}}
													rows="2"
												></textarea>
												<button
												className="send-button-text"
												onClick={() => {
													sendMessage();
												}}
												disabled={!formValues.inputText.trim()}
												aria-label="送信"
												>
												<svg 
													xmlns="http://www.w3.org/2000/svg" 
													width="16" 
													height="16" 
													viewBox="0 0 24 24" 
													fill="none" 
													stroke="currentColor" 
													strokeWidth="2" 
													strokeLinecap="round" 
													strokeLinejoin="round"
												>
													<line x1="22" y1="2" x2="11" y2="13"></line>
													<polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
												</svg>
												</button>
											</div>
												{/* 免責事項テキストを追加 */}
												<div className="ai-disclaimer">
												{t('chatWarn')}
												</div>
											</div>

											{/* フィードバック部分 */}
											<div className="feedback-section">
												<div className="chat-feedback">
													<div className="feedback-label">{t('responseRateText')}</div>
													<div className="feedback-buttons">
														<button 
															className="feedback-button positive" 
															onClick={() => handleReviewClick(1)}
															disabled={!hasInteracted} // やり取りがない場合は非活性
														>
															{t('resolvedBtn')}
														</button>
														<button 
															className="feedback-button negative" 
															onClick={() => handleReviewClick(2)}
															disabled={!hasInteracted} // やり取りがない場合は非活性
														>
															{t('notClearBtn')}
														</button>
													</div>
												</div>
											</div>
										</div>
									</div>
								</>
							)}

							{/* リサイズハンドルを追加（chat-containerの閉じタグの直前） */}
							<div className="resize-handle resize-n" onMouseDown={(e) => handleResizeStart('n', e)} />
							<div className="resize-handle resize-s" onMouseDown={(e) => handleResizeStart('s', e)} />
							<div className="resize-handle resize-e" onMouseDown={(e) => handleResizeStart('e', e)} />
							<div className="resize-handle resize-w" onMouseDown={(e) => handleResizeStart('w', e)} />
							<div className="resize-handle resize-ne" onMouseDown={(e) => handleResizeStart('ne', e)} />
							<div className="resize-handle resize-nw" onMouseDown={(e) => handleResizeStart('nw', e)} />
							<div className="resize-handle resize-se" onMouseDown={(e) => handleResizeStart('se', e)} />
							<div className="resize-handle resize-sw" onMouseDown={(e) => handleResizeStart('sw', e)} />
						</div>
					</>
				)}
				</div>
			</Container>
		);
	}
}
export default Portal;
